import type { HttpClient, RequestOptions } from '../api/http';
import { HttpFailure } from '../api/http-failure';
import { isOnline } from '../pwa/connectivity';
import { noteOutcome } from '../reach/reach.store';
import { isAllowedStoredViewUrl } from '../stored-views/allow-list';

/**
 * 036 (issue 208) — the part of 021 FR-006a `core/http/offline-client.ts`'s deletion left uncovered.
 *
 * **Why this exists at all.** FR-024 forbids UNMOUNTING the route component a screen without
 * `READABLE_OFFLINE`/`WORKS_OFFLINE` sits behind (an open editor's unsaved text would go with it),
 * so that screen's own mount effects go on running — and, before this file, on firing GETs nobody
 * can see the answer to, exactly the waste FR-006a exists to forbid ("the application must not
 * ATTEMPT to load task data" while there is no connection). The deleted `offline-client.ts` was the
 * one thing stopping that for EVERY screen; deleting it outright reopened the gap for the ten
 * screens this slice has not reached yet — caught by `e2e-pwa/pwa.spec.ts`'s own "attempts no
 * task-data request while offline" going red on `/settings` once nothing blocked its capture-context
 * picker's own `/contexts` read.
 *
 * **Why it is not simply the old file restored.** Home's — and `/unsent`'s — own reads must ATTEMPT
 * for real: that attempt is how `core/reach/` learns anything at all (FR-003 forbids deciding
 * reachability from `navigator.onLine` alone) and how `core/stored-views/`'s storing client decides
 * whether to fall back. `isAllowedStoredViewUrl` is the SAME allow-list `storing-client.ts` reads,
 * so a `GET` this cycle has made readable is exempted here for the identical reason it is stored.
 *
 * **Composed OUTSIDE `storing`, `observing` AND `renewing`, in `main.tsx`.** A refusal this file
 * manufactures was never sent, so `observing-client.ts`'s own `noteOutcome` call never runs for it —
 * composing outside `observing` is what keeps that true structurally rather than by care.
 *
 * **This file calls `noteOutcome(0)` itself, though, and that needed a correction to an earlier
 * version of this comment, which claimed the opposite.** A screen with NOTHING allow-listed to
 * attempt (every one but home and `/unsent`, this slice) can go on refusing every read this way
 * forever without a single request ever reaching `observing`, and `core/reach/` has no OTHER source
 * of information about THAT screen at all — found when `e2e-pwa/pwa.spec.ts`'s own "attempts no
 * task-data request" went on to fail a SECOND way, on the SAME run this file's first version fixed
 * the first: `/settings`'s offline panel never appeared, because reach had genuinely never observed
 * anything since the cut and stayed `reachable`. `navigator.onLine` going false is not a probe and
 * not a guess — it is the browser's own report of a real condition, the same fact
 * `core/session/session.store.ts`'s `couldNotReach` already folds in alongside a real status `0` —
 * so treating it as evidence here, for the narrow case where this file is the reason nothing else
 * could have told `core/reach/` anything, is a use of an independent signal rather than a
 * self-fulfilling one. Home's and `/unsent`'s own reads are UNCHANGED by this: they are exempted
 * from this file entirely and go on informing `core/reach/` only through `observing`, from requests
 * that were actually sent.
 *
 * **`GET` only.** A write refused this way and a write that genuinely fails over a real cut network
 * both surface as the identical status-`0` `HttpFailure` — FR-026's controls attempt the network and
 * show the existing failure either way (this slice does not change that), so there is no observable
 * difference to preserve for them, and FR-006a's own wording ("load task data") is about reads.
 */
export function createOfflineGuardClient(inner: HttpClient): HttpClient {
  const refuse = <T,>(url: string): Promise<T> => {
    noteOutcome(0);
    return Promise.reject(
      new HttpFailure({
        status: 0,
        statusText: 'Offline',
        url,
        error: new Error('offline: request not attempted (FR-006a)'),
      }),
    );
  };

  return {
    get: <T,>(url: string, options?: RequestOptions) =>
      (isOnline() || isAllowedStoredViewUrl(url) ? inner.get<T>(url, options) : refuse<T>(url)),
    post: (url, body, options) => inner.post(url, body, options),
    put: (url, body, options) => inner.put(url, body, options),
    patch: (url, body, options) => inner.patch(url, body, options),
    delete: (url, options) => inner.delete(url, options),
  };
}
